import {
  BoxGeometry,
  ConeGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshLambertMaterial,
  type BufferGeometry,
  type Material,
} from 'three';

/**
 * EN-03 Avioneta sicaria (GDD §8.2, issue #85): a dark low-poly plane that cannot be mistaken for La Chiva:
 * charcoal fuselage and olive wing, nose to the left (it flies to the left), no weapons firing and nobody
 * hurt on board (GDD §3.4). The hitbox is 1.4 x 0.5 u, 70-80 % of the model, which spans about 1.8 x 0.65 u.
 */
export interface HitmanPlane {
  group: Group;
  body: Mesh;
  propeller: Mesh;
  dispose(): void;
}

export const HITMAN_COLORS = { body: 0x2b3038, wing: 0x59603a, stripe: 0xb7ad8a, prop: 0x15171b } as const;

export function buildHitmanPlane(): HitmanPlane {
  const geometries: BufferGeometry[] = [];
  const materials: Material[] = [];
  const paint = (color: number) => {
    const material = new MeshLambertMaterial({ color, flatShading: true });
    materials.push(material);
    return material;
  };
  const own = <T extends BufferGeometry>(geometry: T): T => {
    geometries.push(geometry);
    return geometry;
  };

  const group = new Group();
  // Fuselage 1.3 long and 0.4 high, nose cone to the left: 1.8 x 0.65 u with the tail fin and the gear.
  const body = new Mesh(own(new CylinderGeometry(0.2, 0.2, 1.3, 6)), paint(HITMAN_COLORS.body));
  body.name = 'enemy-en03-body';
  body.rotation.z = Math.PI / 2;
  const nose = new Mesh(own(new ConeGeometry(0.2, 0.25, 6)), paint(HITMAN_COLORS.wing));
  nose.rotation.z = Math.PI / 2;
  nose.position.x = -0.775;
  const tail = new Mesh(own(new BoxGeometry(0.3, 0.55, 0.05)), paint(HITMAN_COLORS.body));
  tail.position.set(0.75, 0.15, 0);
  const wing = new Mesh(own(new BoxGeometry(0.5, 0.05, 2)), paint(HITMAN_COLORS.wing));
  wing.position.set(-0.05, 0.18, 0);
  const stripe = new Mesh(own(new CylinderGeometry(0.205, 0.205, 0.2, 6)), paint(HITMAN_COLORS.stripe));
  stripe.rotation.z = Math.PI / 2;
  stripe.position.x = 0.2;
  const gear = new Mesh(own(new BoxGeometry(0.2, 0.12, 0.2)), paint(HITMAN_COLORS.prop));
  gear.position.set(-0.2, -0.25, 0);
  const propeller = new Mesh(own(new BoxGeometry(0.04, 0.65, 0.08)), paint(HITMAN_COLORS.prop));
  propeller.position.x = -0.92;
  group.add(body, nose, tail, wing, stripe, gear, propeller);

  return {
    group,
    body,
    propeller,
    dispose() {
      for (const geometry of geometries) geometry.dispose();
      for (const material of materials) material.dispose();
    },
  };
}
